// ------------------------------------------------------------------------------
// name: uploadChunk
// author: mudas
// created: 2024-10-16
// ------------------------------------------------------------------------------

import { AxiosRequestConfig } from 'axios';
import { transFormData } from 'common/utils';

import { FileACL } from 'common/types';
import { ChunkUploadResult, SingleUploadUrl, ChunkUploadId } from 'common/hooks/upload/types';
import { FastApiAxiosInstance, FosAxiosInstance } from './axiosInstances';

// --------------------------------------------------------------------------
//
// 文件分片上传
//
// --------------------------------------------------------------------------

// 从art-backend服务 获取分片上传的id
export const getUploadChunkId = async (params: {
  uuid?: string;
  fileName: string;
  fileSize: string;
  acl: FileACL;
  path?: string;
  contentType: string;
}) => {
  try {
    const { data } = await FastApiAxiosInstance.post<ChunkUploadId>('/api/v2/file/getChunkUploadId', params);
    return data;
  } catch (e) {
    return null;
  }
};

// 获取上传分片的预签名链接
export const getUploadChunkPreSignUrl = (
  params: { fileId: string; index: number; chunkUploadId: string },
  config?: AxiosRequestConfig<any>,
) => {
  return FastApiAxiosInstance.post<{ preSignedChunkUploadUrl: string }>(
    '/api/v2/file/getPreSignedChunkUploadUrl',
    params,
    { ...config },
  );
};

/**
 * 上传分片
 * @param url 预签名地址（已较验过鉴权，因此当前方法不需要再传递任何权限数据）
 * @param blob 文件切片数据
 * @param config
 */
export const uploadChunk = ({ url, blob }: { url: string; blob: Blob }, config?: AxiosRequestConfig<any>) => {
  const data = transFormData({
    file: blob,
  });

  return FosAxiosInstance.post<{ partEtag: ChunkUploadResult }>(url, data, { ...config });
};

// 获取上传分片的预签名链接
export const chunksConcat = async (params: {
  fileId: string;
  chunkUploadId: string;
  partEtags: ChunkUploadResult[];
}) => {
  return FastApiAxiosInstance.post('/api/v2/file/chunkUploadSuccess', params);
};

// --------------------------------------------------------------------------
//
// 单文件上传
//
// --------------------------------------------------------------------------

//获取上传-预签名链接(单文件)
export const getPreSignedUploadUrl = async (params: {
  uuid?: string;
  fileName: string;
  fileSize: string;
  acl: FileACL;
  path?: string;
  contentType: string;
}) => {
  try {
    const res = await FastApiAxiosInstance.post<SingleUploadUrl>('/api/v2/file/getPreSignedUploadUrl', params);
    return res;
  } catch (e) {
    return null;
  }
};

// --------------------------------------------------------------------------
//
// 单文件、分片文件数据的上传方法（共用）
//
// --------------------------------------------------------------------------

/**
 * 上传单个完整文件数据
 * @param url 预签名地址（已较验过鉴权，因此当前方法不需要再传递任何权限数据）
 * @param blob 文件数据
 * @param config
 */
export const uploadFileData = ({ url, blob }: { url: string; blob: Blob }, config?: AxiosRequestConfig<any>) => {
  // 数源上传单文件要求 put 方法
  // axios 会默认设定 Content-Type 为 'application/x-www-form-urlencoded'，
  // 因此需要手动设置 Content-Type 为文件对应的 type
  // 而 fetch 方法无此问题，应该是会根据 data 自动判断
  return FosAxiosInstance.request({
    url,
    method: 'put',
    data: blob,
    headers: {
      'Content-Type': blob.type,
    },
    ...config,
  });
};
